import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/20/solid'
import { Badge } from 'components/badge'
import { Button } from 'components/button'
import { Divider } from 'components/divider'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { GET_EXPLORE_ITEMS } from 'graphql/exploreItems/queries'
import { t } from 'i18next'
import { formatDateOnly } from 'utils/common'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { Carousel } from 'flowbite-react'

// Category options
const CATEGORY_OPTIONS = [
    { value: 'ATR', label: 'Attraction' },
    { value: 'HOT', label: 'Hotel' },
    { value: 'CAR', label: 'Car Rental' },
]

// Country options
const COUNTRY_OPTIONS = [
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'BH', label: 'Bahrain' },
    { value: 'KW', label: 'Kuwait' },
    { value: 'OM', label: 'Oman' },
    { value: 'QA', label: 'Qatar' },
    { value: 'SA', label: 'Saudi Arabia' },
]

export default function ExploreItemDetails() {
    const navigate = useNavigate()
    const { id } = useParams()
    const { t } = useTranslation()

    // Dialog states
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    // Data state
    const [exploreItem, setExploreItem] = useState(null)

    // GraphQL hook
    const [getExploreItem, { loading: getLoading }] = useLazyQuery(GET_EXPLORE_ITEMS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getExploreItems && data.getExploreItems.success && data.getExploreItems.explore_item) {
                const item = data.getExploreItems.explore_item[0] // Get first item from array
                if (item) {
                    setExploreItem(item)
                }
            } else if (data.getExploreItems && !data.getExploreItems.success) {
                setErrorMessage(data.getExploreItems.message || 'Failed to fetch explore item')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    // Load data
    useEffect(() => {
        if (id) {
            getExploreItem({
                variables: { 
                    itemId: id,
                    page: { limit: 1, offset: 0 }
                },
            })
        }
    }, [id, getExploreItem])

    const handleDialogClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    const getCategoryLabel = (categoryCode) => {
        const category = CATEGORY_OPTIONS.find(opt => opt.value === categoryCode)
        return category ? category.label : categoryCode
    }

    const getCountryLabel = (countryCode) => {
        const country = COUNTRY_OPTIONS.find(opt => opt.value === countryCode)
        return country ? country.label : countryCode
    }

    if (getLoading) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <img alt="logo" src={loadingImage} className="w-[250px]" />
                <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
            </div>
        )
    }

    if (!exploreItem) {
        return (
            <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('Explore item not found!')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('The requested explore item could not be found')}</p>
            </div>
        )
    }

    return (
        <>
            <div className="mb-6 flex justify-normal items-center">
                <button
                    onClick={() => navigate('/explore-items')}
                    type="button"
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1">Explore Item Details</h3>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="md:flex md:items-center md:justify-between md:space-x-5">
                        <div className="flex items-start space-x-5">
                            <div className="flex-shrink-0">
                                <div className="relative">
                                    <ImageDisplay
                                        s3Key={exploreItem.content?.cover_image?.mobile?.s3_key}
                                        file=""
                                        classStyle="h-16 w-16 rounded-lg"
                                    />
                                    <span aria-hidden="true" className="absolute inset-0 rounded-lg shadow-inner" />
                                </div>
                            </div>
                            <div className="pt-1.5">
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {exploreItem.content?.title?.en || 'N/A'}
                                </h1>
                                <p className="text-sm font-medium text-gray-500">
                                    {getCategoryLabel(exploreItem.category_code)} • {getCountryLabel(exploreItem.country_code)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
                            <a
                                href={`/explore-items/edit/${id}`}
                                className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary inline-flex justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                            >
                                {t('Edit Item')}
                            </a>
                        </div>
                    </div>

                    <div className="mt-8">
                        <div className="px-4 sm:px-0">
                            <h3 className="text-lg font-semibold leading-7 text-primary">
                                {t('Explore Item Information')}
                            </h3>
                            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                {t('Details about the explore item, its pricing, and features.')}
                            </p>
                        </div>
                        <div className="mt-3 border-t border-gray-100">
                            <dl className="">
                                <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Item Title')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        <div className="flex flex-col">
                                            <span className="font-semibold">{exploreItem.content?.title?.en || 'N/A'}</span>
                                            {exploreItem.content?.title?.ar && (
                                                <span className="text-xs text-gray-500">{exploreItem.content.title.ar}</span>
                                            )}
                                        </div>
                                    </dd>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Category')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {getCategoryLabel(exploreItem.category_code)}
                                    </dd>
                                </div>
                                <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Country')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {getCountryLabel(exploreItem.country_code)}
                                    </dd>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        <Badge color={exploreItem.is_active ? 'emerald' : 'rose'}>
                                            {exploreItem.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </dd>
                                </div>
                                <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Price')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {exploreItem.content?.pricing?.price_from 
                                            ? `${exploreItem.content.pricing.price_from} ${exploreItem.content.pricing.currency || ''}`
                                            : 'N/A'
                                        }
                                    </dd>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Contact Number')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {exploreItem.content?.contact_no || 'N/A'}
                                    </dd>
                                </div>
                                <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Link URL')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {exploreItem.content?.link_url ? (
                                            <a 
                                                href={exploreItem.content.link_url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                                {exploreItem.content.link_url}
                                            </a>
                                        ) : 'N/A'}
                                    </dd>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Location')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {exploreItem.content?.location?.city || 'N/A'}
                                        {exploreItem.content?.location?.lat && exploreItem.content?.location?.lng && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                {exploreItem.content.location.lat}, {exploreItem.content.location.lng}
                                            </div>
                                        )}
                                    </dd>
                                </div>
                                <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Valid From')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {formatDateOnly(exploreItem.valid_from)}
                                    </dd>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                        {t('Valid To')}
                                    </dt>
                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {formatDateOnly(exploreItem.valid_to)}
                                    </dd>
                                </div>
                                {(exploreItem.content?.description?.en || exploreItem.content?.description?.ar) && (
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Description')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <div className="space-y-2">
                                                {exploreItem.content.description.en && (
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500">{t('English')}</div>
                                                        <div className="whitespace-pre-wrap">{exploreItem.content.description.en}</div>
                                                    </div>
                                                )}
                                                {exploreItem.content.description.ar && (
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500">{t('Arabic')}</div>
                                                        <div className="whitespace-pre-wrap">{exploreItem.content.description.ar}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </dd>
                                    </div>
                                )}
                                {exploreItem.user_audit && (
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Created By')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {exploreItem.user_audit.created_by || 'N/A'}
                                            {exploreItem.user_audit.created_date && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {new Date(exploreItem.user_audit.created_date).toLocaleString()}
                                                </div>
                                            )}
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </div>
                    </div>

                    {/* Images Section */}
                    {(exploreItem.content?.images && exploreItem.content.images.length > 0) && (
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Photos')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <div className="mt-4">
                                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                        {exploreItem.content.images.map((image, index) => (
                                            <div key={index} className="group relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                                                <div className="aspect-w-16 aspect-h-12 overflow-hidden rounded-lg bg-gray-100">
                                                    <ImageDisplay
                                                        s3Key={image.mobile?.s3_key || image.tablet?.s3_key}
                                                        file=""
                                                        classStyle="h-full w-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                    />
                                                </div>
                                                <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-gray-900/10" />
                                                <div className="mt-2 px-3 pb-3">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {t('Image')} {index + 1}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {image.mobile?.file_name || image.tablet?.file_name || 'Image file'}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Cover Image Display */}
                                    {exploreItem.content?.cover_image && (
                                        <div className="mt-8">
                                            <h4 className="text-md font-medium leading-6 text-gray-900 mb-4">
                                                {t('Cover Image')}
                                            </h4>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div className="group relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                                                    <div className="aspect-w-16 aspect-h-12 overflow-hidden rounded-lg bg-gray-100">
                                                        <ImageDisplay
                                                            s3Key={exploreItem.content.cover_image.mobile?.s3_key}
                                                            file=""
                                                            classStyle="h-full w-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                        />
                                                    </div>
                                                    <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-gray-900/10" />
                                                    <div className="mt-2 px-3 pb-3">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {t('Mobile Cover')}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {exploreItem.content.cover_image.mobile?.file_name || 'Cover image'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="group relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                                                    <div className="aspect-w-16 aspect-h-12 overflow-hidden rounded-lg bg-gray-100">
                                                        <ImageDisplay
                                                            s3Key={exploreItem.content.cover_image.tablet?.s3_key}
                                                            file=""
                                                            classStyle="h-full w-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                        />
                                                    </div>
                                                    <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-gray-900/10" />
                                                    <div className="mt-2 px-3 pb-3">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {t('Tablet Cover')}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {exploreItem.content.cover_image.tablet?.file_name || 'Cover image'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
        </>
    )
}
